import { useRoute } from '#imports'
import { computed } from 'vue'

import { urlHashToObject } from '@backmarket/utils/url/urlHashToObject'

import {
  HASH_MESSAGE_KEY,
  HASH_MESSAGE_SOURCE_KEY,
  HASH_OPEN_KEY,
} from '../utils/removeSeededMessageFromHash'
import { sanitizeSeededMessage } from '../utils/sanitizeSeededMessage'

/**
 * Reads a seeded launch out of the URL hash, for the web-sidebar host. Unlike
 * the web-page host's query params, this has to stay reactive to route.hash
 * rather than only being read once on mount, to support onsite links that
 * change the hash via client-side navigation without necessarily remounting
 * the host.
 */
export function useShoppingAssistantHashContext() {
  const route = useRoute()

  const seededMessage = computed<string>(() => {
    const values = urlHashToObject(route.hash)[HASH_MESSAGE_KEY]

    return sanitizeSeededMessage(values?.length === 1 ? values[0] : undefined)
  })

  const entrySource = computed<string>(() => {
    const values = urlHashToObject(route.hash)[HASH_MESSAGE_SOURCE_KEY]

    return values?.length === 1 ? values[0].trim() : ''
  })

  // Bare open trigger: #assistant_open (with or without a value, e.g.
  // #assistant_open, #assistant_open=true). A repeated key is ignored, the
  // same guard as seededMessage/entrySource above — query-string surfaces a
  // bare key as null, which urlHashToObject wraps into a single-element
  // array, so the length check still holds.
  const isOpenTrigger = computed<boolean>(() => {
    const values = urlHashToObject(route.hash)[HASH_OPEN_KEY]

    return values?.length === 1
  })

  const isSeededLaunch = computed<boolean>(() => seededMessage.value !== '')

  return {
    seededMessage,
    entrySource,
    isOpenTrigger,
    isSeededLaunch,
  }
}
